import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

const LessonDetail = () => {
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();
  const [lessonData, setLessonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chunks, setChunks] = useState([]);

  // 모듈 정보
  const modules = {
    'LM2001020201_요구사항확인': {
      name: '요구사항 확인',
      description: 'NCS 학습모듈 - 요구사항 확인'
    }
  };

  useEffect(() => {
    loadLessonData();
  }, [moduleId, lessonId]);

  const loadLessonData = async () => {
    try {
      setLoading(true);
      
      // JSONL 파일에서 데이터 로드
      const response = await fetch('/final_chunks.jsonl');
      const text = await response.text();
      
      const allChunks = text
        .split('\n')
        .filter(line => line.trim())
        .map(line => JSON.parse(line));
      
      // 해당 모듈과 학습에 맞는 청크들 필터링
      const lessonChunks = allChunks.filter(chunk => 
        chunk.doc_id === moduleId && chunk.lesson_num === lessonId
      );
      
      if (lessonChunks.length === 0) {
        // 더미 데이터로 대체
        generateDummyLessonData();
        return;
      }
      
      setChunks(lessonChunks);
      procesLessonData(lessonChunks);
      
    } catch (error) {
      console.error('학습 데이터 로드 실패:', error);
      generateDummyLessonData();
    } finally {
      setLoading(false);
    }
  };

  const procesLessonData = (chunks) => {
    if (chunks.length === 0) return;
    
    // 첫 번째 청크에서 기본 정보 추출
    const firstChunk = chunks[0];
    
    // 토픽별로 그룹화
    const topicGroups = chunks.reduce((acc, chunk) => {
      const topicId = chunk.topic_id;
      if (!acc[topicId]) {
        acc[topicId] = {
          id: topicId,
          title: chunk.topic_title,
          chunks: []
        };
      }
      acc[topicId].chunks.push(chunk);
      return acc;
    }, {});

    // 학습 데이터 구성
    const lesson = {
      id: lessonId,
      title: firstChunk.lesson_title,
      totalChunks: chunks.length,
      totalLength: chunks.reduce((sum, chunk) => sum + chunk.length, 0),
      topics: Object.values(topicGroups),
      keyTerms: extractKeyTermsFromChunks(chunks),
      contentTypes: extractContentTypes(chunks)
    };

    setLessonData(lesson);
  };

  const generateDummyLessonData = () => {
    const dummyLessons = {
      '1': {
        id: '1',
        title: '현행 시스템 분석하기',
        totalChunks: 4,
        totalLength: 2450,
        topics: [
          {
            id: '1-1',
            title: '현행 시스템 파악',
            chunks: [
              {
                text: '현행 시스템 파악은 개발하고자 하는 응용소프트웨어에 대한 이해를 높이기 위해 필요한 과정입니다. 현행 시스템의 구성, 기능, 인터페이스 현황을 체계적으로 분석하여 향후 개발될 시스템의 방향성을 설정합니다.',
                annotations: { type: ['정의'], has_example: false }
              }
            ]
          },
          {
            id: '1-2',
            title: '시스템 구성 현황 분석',
            chunks: [
              {
                text: '시스템 구성 현황 분석에서는 하드웨어, 소프트웨어, 네트워크 구성을 상세히 파악합니다. 서버의 사양, 데이터베이스 구조, 보안 체계 등을 종합적으로 검토하여 현재 상태를 문서화합니다.',
                annotations: { type: ['절차'], has_example: true }
              }
            ]
          }
        ],
        keyTerms: ['현행시스템', '시스템분석', '구성현황', '인터페이스'],
        contentTypes: ['정의', '절차']
      },
      '2': {
        id: '2',
        title: '요구사항 정의',
        totalChunks: 3,
        totalLength: 1890,
        topics: [
          {
            id: '2-1',
            title: '요구사항 정의 개요',
            chunks: [
              {
                text: '요구사항 정의는 시스템이 제공해야 할 기능과 성능, 제약사항을 명확히 규정하는 과정입니다. 사용자의 요구를 체계적으로 수집하고 분석하여 개발 가능한 형태로 정리합니다.',
                annotations: { type: ['정의'], has_example: false }
              }
            ]
          }
        ],
        keyTerms: ['요구사항', '기능요구사항', '비기능요구사항'],
        contentTypes: ['정의']
      },
      '3': {
        id: '3',
        title: '분석모델 확인하기',
        totalChunks: 67,
        totalLength: 15420,
        topics: [
          {
            id: '3-1',
            title: '분석모델 검증',
            chunks: [
              {
                text: '분석모델 검증은 작성된 요구사항과 설계 모델의 정확성, 완전성, 일관성을 확인하는 과정입니다. 다양한 검증 기법을 활용하여 모델의 품질을 보장합니다.',
                annotations: { type: ['절차'], has_example: true }
              }
            ]
          }
        ],
        keyTerms: ['분석모델', '검증', '타당성분석'],
        contentTypes: ['절차', '예시']
      }
    };

    setLessonData(dummyLessons[lessonId] || dummyLessons['1']);
  };

  const extractKeyTermsFromChunks = (chunks) => {
    const allTerms = chunks.flatMap(chunk => {
      const text = chunk.text;
      const techTerms = text.match(/[가-힣]{2,}(?:시스템|모델|방법|기법|분석|설계|구현|테스트|관리|요구사항)/g) || [];
      return techTerms;
    });
    
    return [...new Set(allTerms)].slice(0, 10);
  };

  const extractContentTypes = (chunks) => {
    const types = chunks.flatMap(chunk => 
      chunk.annotations?.type || []
    );
    return [...new Set(types)];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">학습 내용을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (!lessonData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">❌</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">학습 내용을 찾을 수 없습니다</h2>
          <p className="text-gray-600 mb-4">요청하신 학습 내용이 존재하지 않습니다.</p>
          <Link
            to="/summary"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ← 요약정리로 돌아가기
          </Link>
        </div>
      </div>
    );
  }

  const moduleInfo = modules[moduleId];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* 브레드크럼 & 뒤로가기 */}
        <div className="mb-6">
          <nav className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
            <Link to="/summary" className="hover:text-blue-600 transition-colors">
              요약정리
            </Link>
            <span>›</span>
            <span className="text-gray-900">{moduleInfo?.name}</span>
            <span>›</span>
            <span className="text-gray-900">학습 {lessonData.id}</span>
          </nav>
          
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            ← 뒤로가기
          </button>
        </div>

        {/* 학습 헤더 */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl p-8 mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-4">
                학습 {lessonData.id}. {lessonData.title}
              </h1>
              <p className="text-blue-100 mb-4">
                {moduleInfo?.description}
              </p>
              <div className="flex gap-6 text-blue-100">
                <span>📑 {lessonData.totalChunks}개 섹션</span>
                <span>🎯 {lessonData.topics.length}개 토픽</span>
                <span>📝 {lessonData.totalLength.toLocaleString()}글자</span>
              </div>
            </div>
          </div>
        </div>

        {/* 주요 키워드 */}
        {lessonData.keyTerms.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">🔑 주요 키워드</h2>
            <div className="flex flex-wrap gap-2">
              {lessonData.keyTerms.map((term, index) => (
                <span
                  key={index}
                  className="px-3 py-2 bg-blue-100 text-blue-800 rounded-lg font-medium"
                >
                  {term}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* 토픽별 상세 내용 */}
        <div className="space-y-8">
          <h2 className="text-2xl font-bold text-gray-900">📋 토픽별 상세 내용</h2>
          
          {lessonData.topics.map((topic, index) => (
            <div key={topic.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* 토픽 헤더 */}
              <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-6">
                <h3 className="text-xl font-bold">
                  {topic.id}. {topic.title}
                </h3>
                <p className="text-green-100 mt-2">
                  {topic.chunks.length}개 섹션
                </p>
              </div>

              {/* 토픽 내용 */}
              <div className="p-6 space-y-6">
                {topic.chunks.map((chunk, chunkIndex) => (
                  <div key={chunkIndex} className="border-l-4 border-blue-200 pl-6 py-4">
                    <div className="prose max-w-none">
                      <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                        {chunk.text}
                      </p>
                    </div>
                    
                    {/* 청크 메타 정보 */}
                    <div className="mt-4 flex gap-4 text-sm">
                      {chunk.annotations?.type && chunk.annotations.type.length > 0 && (
                        <div className="flex gap-1">
                          {chunk.annotations.type.map((type, typeIndex) => (
                            <span
                              key={typeIndex}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {chunk.annotations?.has_example && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded">
                          예시 포함
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* 학습 완료 버튼 */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              🎯 학습 {lessonData.id} 완료
            </h3>
            <p className="text-gray-600 mb-6">
              이 학습 단위의 모든 내용을 확인하셨습니다.
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/summary"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                다른 학습 보기
              </Link>
              <Link
                to="/quiz"
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                퀴즈 풀어보기
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonDetail;